import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchClientes, fetchEstadoClientes } from "redux/actions/clientesActions";
import { getRUCEmpresa } from "redux/actions/userActions";

const CLIENTES_PAGE_SIZE = 15;
const CLIENTES_SORT = "estado,fechaRegistro,desc";

const getValue = (source, keys, fallback = "") => {
  if (!source || typeof source !== "object") return fallback;

  const key = keys.find(
    (item) => source[item] !== undefined && source[item] !== null,
  );
  return key ? source[key] : fallback;
};

const normalizeNumber = (value) => {
  if (value === undefined || value === null || value === "") return "0";
  if (typeof value === "string") return value;

  return Number(value).toLocaleString("en-US");
};

const getClientesPayload = (data) => {
  if (!data) return {};
  if (Array.isArray(data)) return { clientes: data };
  if (Array.isArray(data.content)) return { ...data, clientes: data.content };
  if (Array.isArray(data.data)) return { ...data, clientes: data.data };
  if (data.data && typeof data.data === "object") return data.data;

  return data;
};

const getClientesList = (payload) => {
  const list = getValue(
    payload,
    ["clientes", "items", "content", "listaClientes", "detalle"],
    [],
  );

  return Array.isArray(list) ? list : [];
};

const isActiveClient = (client) => {
  const status = getValue(client, ["estado", "activo", "status"], false);

  if (typeof status === "boolean") return status;
  if (typeof status === "number") return status === 1;
  if (typeof status === "string") {
    return ["activo", "activa", "true", "1", "a"].includes(status.toLowerCase());
  }

  return false;
};

const buildStats = (payload, clientes) => {
  const total = getValue(
    payload,
    ["totalClientes", "clientesTotal", "total", "cantidadClientes"],
    clientes.length,
  );
  const activos = getValue(
    payload,
    ["clientesActivos", "activos", "totalActivos"],
    clientes.filter(isActiveClient).length,
  );
  const nuevos = getValue(
    payload,
    ["nuevosEsteMes", "clientesNuevosMes", "nuevosMes", "totalNuevos"],
    0,
  );

  return [
    {
      label: "TOTAL CLIENTES",
      value: normalizeNumber(total),
      detail: "Registrados",
      tone: "total",
    },
    {
      label: "ACTIVOS",
      value: normalizeNumber(activos),
      detail: "Con al menos 1 factura",
      tone: "active",
    },
    {
      label: "NUEVOS ESTE MES",
      value: normalizeNumber(nuevos),
      detail: getValue(payload, ["detalleNuevos", "periodoNuevos"], "Este mes"),
      tone: "new",
    },
  ];
};

const normalizeClient = (client, index) => {
  const cliente = client?.cliente || {};

  return {
    id: getValue(
      client,
      ["idAsignacionEmpresaCliente", "id", "idCliente"],
      cliente.idCliente || index,
    ),
    ruc: getValue(client, ["ruc", "identificacion", "idCliente"], cliente.idCliente || ""),
    razonSocial: getValue(client, ["razonSocial", "nombre", "nombres"], cliente.nombres || ""),
    email: getValue(client, ["email", "correo"], ""),
    telefono: getValue(client, ["telefono", "celular"], ""),
    direccion: getValue(client, ["direccion"], ""),
    fechaRegistro: getValue(client, ["fechaRegistro"], cliente.fechaRegistro || ""),
    facturas: getValue(client, ["facturas", "totalFacturas", "cantidadFacturas"], 0),
    activo: isActiveClient(client),
  };
};

const filterClientes = (clientes, search) => {
  const term = search.trim().toLowerCase();

  if (!term) return clientes;

  return clientes.filter((client) =>
    [
      client.ruc,
      client.razonSocial,
      client.email,
      client.telefono,
      client.direccion,
    ].some((value) => String(value).toLowerCase().includes(term)),
  );
};

const useEstadoClientes = () => {
  const dispatch = useDispatch();
  const tokenUser = useSelector((state) => state.user.tk);
  const estado = useSelector((state) => state.clientes.estado);
  const list = useSelector((state) => state.clientes.list);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(CLIENTES_PAGE_SIZE);

  useEffect(() => {
    if (!tokenUser) return;
    dispatch(fetchEstadoClientes(getRUCEmpresa(tokenUser))).catch(() => {});
  }, [dispatch, tokenUser]);

  useEffect(() => {
    if (!tokenUser) return;
    dispatch(
      fetchClientes({
        elementos: rowsPerPage,
        page,
        rucUsuario: getRUCEmpresa(tokenUser),
        sort: CLIENTES_SORT,
      }),
    ).catch(() => {});
  }, [dispatch, tokenUser, page, rowsPerPage]);

  const handlePageChange = (_event, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const payload = useMemo(() => getClientesPayload(estado.data), [estado.data]);
  const listPayload = useMemo(() => getClientesPayload(list.data), [list.data]);
  const clientes = useMemo(() => getClientesList(listPayload), [listPayload]);
  const totalElements = listPayload.totalElements || 0;
  const rows = useMemo(
    () => filterClientes(clientes.map(normalizeClient), search),
    [clientes, search],
  );
  const stats = useMemo(() => buildStats(payload, clientes), [payload, clientes]);

  return {
    error: list.error,
    loading: list.loading,
    rows,
    search,
    setSearch,
    stats,
    page,
    rowsPerPage,
    totalElements,
    onPageChange: handlePageChange,
    onRowsPerPageChange: handleRowsPerPageChange,
  };
};

export default useEstadoClientes;
